import { useRef } from 'react';

import {
  detailsStateClosedClass,
  detailsStateOpenClass,
} from '#/components/Details/detailsClasses';
import { isClientHydrated } from '#/rendering/state/renderingState';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import {
  richDetailsSingletonAttr,
  richDetailsAttr,
  trackClickedAttr,
} from './snippet-attributes';
import { getTrackClickedDataFromElement } from './track-clicked-snippet';

export function useRichInitialDetailsStateClosedClassRef() {
  // We only set this class manually once, make sure it is also present for lazy-loaded
  // elements and elements created after client-side navigation
  return useRef(isClientHydrated() ? detailsStateClosedClass : undefined);
}

export function initializeRichDetails() {
  document
    .querySelectorAll<HTMLDetailsElement>(`details[${richDetailsAttr}]`)
    .forEach((details) => {
      if (!details.open) details.classList.add(detailsStateClosedClass);
    });
}

export function closeRichDetailsSingletonsOnClickOutside(target: Element) {
  document
    .querySelectorAll<HTMLDetailsElement>(
      `details[${richDetailsSingletonAttr}][open]`,
    )
    .forEach((details) => {
      if (!details.contains(target)) {
        // Wait just a little bit because when going from one dropdown to another,
        // closing the previous dropdown gives a flickering impression
        setTimeout(() => {
          // eslint-disable-next-line no-param-reassign
          details.open = false;
        }, 25);
      }
    });
}

export function richDetailsToggled(details: HTMLDetailsElement) {
  if (details.open) {
    if (details.hasAttribute(richDetailsSingletonAttr)) {
      closeRichDetailsSingletonsOnClickOutside(details);
    }

    setTimeout(() => {
      details.classList.replace(detailsStateClosedClass, detailsStateOpenClass);
    }, 10);

    if (details.hasAttribute(trackClickedAttr)) {
      const data = getTrackClickedDataFromElement(details);
      if (data) {
        trackClicked(data.name, data.properties);
      }
    }
  } else {
    details.classList.add(detailsStateClosedClass);
    details.classList.remove(detailsStateOpenClass);
  }
}

export function richDetailsTriggeredByHover(details: HTMLDetailsElement) {
  // eslint-disable-next-line no-param-reassign
  details.open = true;

  let leftTimeout: NodeJS.Timeout | undefined = undefined;

  const onMouseOver = () => {
    clearTimeout(leftTimeout);
  };

  const onMouseOut = () => {
    leftTimeout = setTimeout(() => {
      // eslint-disable-next-line no-param-reassign
      details.open = false;

      details.removeEventListener('mouseover', onMouseOver);
      details.removeEventListener('mouseout', onMouseOut);
    }, 200);
  };

  details.addEventListener('mouseover', onMouseOver);
  details.addEventListener('mouseout', onMouseOut);
}
